import { IChatParticipant, IPatient } from '@dataTypes/chat.type'
import { IContact, IContactGroup } from '@dataTypes/contact.type'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface ModalState {
  group: boolean
  manageGroup: boolean
  block: boolean
  appointment: boolean
  activeCall: boolean
  addNote: boolean
  delete: boolean
  deleteChat: boolean
}

interface MessageCenterState {
  activeTab: string
  show: ModalState
  chatList: string[]
  contactList: IContact[]
  patient: IPatient | null
  channel: string
  contact: IContact | null
  group: IContactGroup | null
  participant: IChatParticipant | null
  toaster: string | null
  lastActiveConversation: string
  lastBlockedUser: string
  inputNumber: string
  conversationForNewGroup: string | null
}

const initialState: MessageCenterState = {
  activeTab: 'contact',
  show: {
    group: false,
    manageGroup: false,
    block: false,
    appointment: false,
    activeCall: false,
    addNote: false,
    delete: false,
    deleteChat: false,
  },
  chatList: [],
  contactList: [],
  patient: null,
  contact: null,
  channel: '',
  group: null,
  participant: null,
  toaster: null,
  lastActiveConversation: '',
  lastBlockedUser: '',
  inputNumber: '',
  conversationForNewGroup: null,
}

const messageCenterSlice = createSlice({
  name: 'messageCenter',
  initialState,
  reducers: {
    SET_ACTIVE_TAB(state, action: PayloadAction<string>) {
      state.activeTab = action.payload
    },
    SET_SHOW_MODAL(state, action: PayloadAction<string | Partial<ModalState>>) {
      // If it's a string, set that modal to true and others to false
      if (typeof action.payload === 'string') {
        const modalKeys: (keyof ModalState)[] = [
          'group',
          'manageGroup',
          'block',
          'appointment',
          'activeCall',
          'addNote',
          'delete',
          'deleteChat',
        ]
        modalKeys.forEach((key) => {
          state.show[key] = action.payload === key
        })
      } else {
        // If it's an object, merge it with the current state
        Object.assign(state.show, action.payload)
      }
    },
    SET_CONTACT_LIST(state, action: PayloadAction<IContact[]>) {
      const seenIds = new Set<string>()
      state.contactList = action.payload.filter((contact) => {
        const id = contact.contact_id
        if (!id) return true
        if (seenIds.has(id)) return false
        seenIds.add(id)
        return true
      })
    },
    SET_CHANNEL(state, action: PayloadAction<string>) {
      state.channel = action.payload
    },
    SET_PARTICIPANT(state, action: PayloadAction<IChatParticipant | null>) {
      state.participant = action.payload
    },
    SET_LAST_ACTIVE_CONVERSATION(state, action: PayloadAction<string>) {
      state.lastActiveConversation = action.payload
    },
    SET_LAST_BLOCKED_USER(state, action: PayloadAction<string>) {
      state.lastBlockedUser = action.payload
    },
    SET_PATIENT(state, action: PayloadAction<IPatient | null>) {
      state.patient = action.payload
    },
    SET_INPUT_NUMBER(state, action: PayloadAction<string>) {
      state.inputNumber = action.payload
    },
    SET_CONTACT(state, action: PayloadAction<IContact | null>) {
      state.contact = action.payload
    },
    SET_CONTACT_GROUP(state, action: PayloadAction<IContactGroup | null>) {
      state.group = action.payload
    },
    SET_CHAT_LIST(state, action: PayloadAction<string[]>) {
      state.chatList = action.payload
    },
    SET_CONVERSATION_FOR_NEW_GROUP(
      state,
      action: PayloadAction<string | null>,
    ) {
      state.conversationForNewGroup = action.payload
    },
  },
})

export const {
  SET_ACTIVE_TAB,
  SET_SHOW_MODAL,
  SET_CONTACT_LIST,
  SET_CHANNEL,
  SET_PARTICIPANT,
  SET_LAST_ACTIVE_CONVERSATION,
  SET_LAST_BLOCKED_USER,
  SET_PATIENT,
  SET_INPUT_NUMBER,
  SET_CONTACT,
  SET_CONTACT_GROUP,
  SET_CHAT_LIST,
  SET_CONVERSATION_FOR_NEW_GROUP,
} = messageCenterSlice.actions

export default messageCenterSlice.reducer
